import { useCallback, useEffect, useRef, useState } from 'react';
import { bridgeApi, suppliersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { FileSpreadsheet, Upload, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '../../components/Layout';

/**
 * Bringing a venue's supplier book in from the spreadsheet they already keep.
 *
 * The least glamorous of the Bridge importers and the one a venue notices first, because
 * nobody can raise a purchase order until it is done. It is built on the menu importer's
 * bones, and differs in one thing worth the screen space: a supplier book is a list a
 * person maintains by hand and re-exports whenever it changes, so the second import is
 * nearly always a correction of the first. The commit matches on the supplier's name and
 * updates rather than inserting, and this page says which rows are which **before** the
 * button is pressed — "nine new, three updated" is the sentence somebody needs in order to
 * press it without worrying.
 *
 * The other thing it says out loud is what is *not* in the file. A supplier with no email
 * or phone is a name in a list; a file with no terms column lands every supplier on Net 30,
 * which is not a guess so much as an invention. Both are cheap to say here and expensive to
 * discover in the Suppliers page a week later.
 */

const gbp = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
  .format(Number(n) || 0);

/**
 * What a cell with nothing behind it should say, which depends on what is about to happen
 * to the row. A new supplier with no terms in the file really does land on Net 30 and the
 * cell should say so. One already in the book keeps what it has, because the commit writes
 * only what a row **stated** — the column present and the cell filled in — so a blank cell
 * on an update is genuinely silence and "unchanged" is the honest word for it.
 *
 * Every empty cell on an update row says this, not just the ones that used to. A dash reads
 * as "nothing here", which against a supplier who has a lead time on file is the same
 * wrong promise in quieter clothes.
 */
const absent = (fate, fallback = '\u2014') => (fate === 'update' ? 'unchanged' : fallback);

/**
 * A number cell as a number, or null when it isn't one. "n/a" in a lead-time column is
 * somebody saying they don't know; the commit treats it as it treats a blank, so the screen
 * does too rather than printing "n/ad" beside a supplier whose lead time is staying put.
 */
const numberIn = (cell) => {
  const text = String(cell ?? '').replace(/[£$€,]/g, '').trim();
  return text !== '' && Number.isFinite(Number(text)) ? Number(text) : null;
};

//: The fields that change what a venue can do with a supplier, and what it costs them to
//: leave one out. Anything not named here is a nice-to-have.
const LOAD_BEARING = [
  { field: 'name', label: 'Supplier name', missing: 'nothing can be imported without it' },
  { field: 'email', label: 'Email', missing: 'you cannot send them an order from Verlo' },
  { field: 'phone', label: 'Phone', missing: 'nobody can ring them from the stock screen' },
  { field: 'payment_terms', label: 'Payment terms', missing: 'new suppliers land on Net 30' },
  { field: 'category', label: 'Category', missing: 'they will not group on the suppliers page' },
];

const SuppliersImportPage = () => {
  usePageTitle('Import suppliers');   // the top line has the title: no second one below
  const fileRef = useRef(null);
  const [record, setRecord] = useState(null);
  const [rows, setRows] = useState([]);
  const [dropped, setDropped] = useState(new Set());
  const [book, setBook] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const mapping = record?.auto_mapped || {};
  const targets = new Set(Object.values(mapping));

  // The names already in their book, folded the same way the commit folds them, so this
  // screen's "new or updated" and the commit's agree. If that rule ever changes it changes
  // in `commit_import_to_suppliers`, and this is the other half of it.
  useEffect(() => {
    suppliersApi.getAll()
      .then(({ data }) => setBook(new Set((data || []).map((s) => (s.name || '').trim().toLowerCase()))))
      .catch(() => setBook(new Set()));
  }, []);

  /** The rows endpoint hands back the file as it was written, keyed by the venue's own
   *  column names, plus the mapping it worked out. Projecting one through the other is what
   *  lets this screen show the supplier exactly as it will land. */
  const read = useCallback(async (id) => {
    const { data } = await bridgeApi.getImportRows(id);
    const map = data.auto_mapped || {};
    setRows((data.rows || []).map((entry) => {
      const raw = entry.row || entry;
      const out = { _error: Boolean(entry.has_error) };
      Object.entries(map).forEach(([source, target]) => { out[target] = raw[source]; });
      return out;
    }));
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setDone(null);
    try {
      const res = await bridgeApi.upload('suppliers', file);
      setRecord(res.data);
      setDropped(new Set());
      await read(res.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That file couldn't be read");
      setRecord(null);
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (i) => setDropped((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const commit = async () => {
    setBusy(true);
    try {
      const accepted = rows.map((_, i) => i).filter((i) => !dropped.has(i));
      const res = await bridgeApi.commitToSuppliers(record.id, { accepted_row_indexes: accepted });
      setDone(res.data);
      const { created, updated } = res.data;
      toast.success([
        created ? `${created} added` : null,
        updated ? `${updated} updated` : null,
      ].filter(Boolean).join(', ') || 'Nothing to change');
      // Their book has moved on, so the next file is read against what is there now.
      const fresh = await suppliersApi.getAll();
      setBook(new Set((fresh.data || []).map((s) => (s.name || '').trim().toLowerCase())));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add them to your suppliers", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  /** What the commit will do with each kept row, worked out the way the commit works it
   *  out: fold the name, and the first row to claim a name is the one that is written.
   *
   *  The second half of that rule is the one worth having on screen. A supplier book
   *  maintained by hand very often lists the same supplier twice, and the commit skips the
   *  repeat; a screen that counted both would promise one more supplier than it saves and
   *  leave somebody hunting for the missing one. */
  const plan = (() => {
    const out = new Map();
    const seen = new Set();
    rows.forEach((row, i) => {
      if (dropped.has(i)) return;
      const name = String(row.name || '').trim().toLowerCase();
      if (!name) { out.set(i, 'nameless'); return; }
      if (seen.has(name)) { out.set(i, 'duplicate'); return; }
      seen.add(name);
      out.set(i, book && book.has(name) ? 'update' : 'add');
    });
    return out;
  })();

  const count = (what) => [...plan.values()].filter((v) => v === what).length;
  const adding = count('add');
  const updating = count('update');
  const writing = adding + updating;

  return (
    <div className="space-y-6" data-testid="suppliers-import-page">
      <div className="space-y-1">
        <p className="max-w-2xl text-[14px] text-ink-soft">
          The list you already keep: a spreadsheet, or an export from your last
          system. We&rsquo;ll work out the columns and show you who is new and who you
          already have before anything is saved.
        </p>
      </div>

      {/* Upload */}
      <div className="rounded-lg border border-hairline bg-surface p-5">
        <input
          id="suppliers-import-file"
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="sr-only"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="suppliers-import-choose">
            <Upload size={15} className="mr-1.5" />
            {record ? 'Choose a different file' : 'Choose a file'}
          </Button>
          <span className="text-[13px] text-ink-faint">
            {record
              ? <><FileSpreadsheet size={13} className="mr-1 inline" />{record.filename || 'spreadsheet'} &middot; {record.row_count} rows</>
              : 'CSV or Excel'}
          </span>
        </div>
      </div>

      {busy && !record && <Skeleton className="h-40 w-full" />}

      {record && (
        <>
          {/* What we made of the columns */}
          <div className="rounded-lg border border-hairline bg-surface p-5 space-y-4" data-testid="suppliers-import-mapping">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-heading text-[15px] font-bold text-ink">What we made of your columns</h2>
              <span className="font-mono text-[12.5px] tabular-nums text-ink-faint">
                {Object.keys(mapping).length} of {(record.columns || []).length} columns understood
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(mapping).map(([source, target]) => (
                <span key={source} className="border border-hairline-strong bg-surface-2 px-2 py-1 text-[12px]">
                  <span className="text-ink-faint">{source}</span>
                  <span className="mx-1.5 text-ink-faint">&rarr;</span>
                  <span className="font-semibold text-ink">{target.replace(/_/g, ' ')}</span>
                </span>
              ))}
            </div>

            {/* What is absent, and what it costs them — before they commit, not after. */}
            {LOAD_BEARING.some((f) => !targets.has(f.field)) && (
              <Notice tone="warn" word="Not in this file" className="text-[13px]">
                <ul className="space-y-0.5">
                  {LOAD_BEARING.filter((f) => !targets.has(f.field)).map((f) => (
                    <li key={f.field}><b>{f.label}</b>: {f.missing}.</li>
                  ))}
                </ul>
                You can import anyway and fill these in later.
              </Notice>
            )}

            {(record.errors || []).length > 0 && (
              <Notice tone="stop" word="Rows to look at" className="text-[13px]">
                {record.errors.slice(0, 5).map((e, i) => (
                  <div key={i}>Row {e.row}: {e.message}</div>
                ))}
                {record.errors.length > 5 && <div>&hellip;and {record.errors.length - 5} more.</div>}
              </Notice>
            )}
          </div>

          {/* The rows, as they will land */}
          <div className="rounded-lg border border-hairline bg-surface" data-testid="suppliers-import-rows">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-5 py-3">
              <h2 className="font-heading text-[15px] font-bold text-ink" data-testid="suppliers-import-headline">
                {adding} to add{updating > 0 ? `, ${updating} to update` : ''}
              </h2>
              <span className="text-[12.5px] text-ink-faint">Untick anything you don&rsquo;t want</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]" style={{ minWidth: '680px' }}>
                <thead>
                  <tr className="text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                    <th className="w-10 px-5 py-2" />
                    <th className="py-2 pr-4">Supplier</th>
                    <th className="py-2 pr-4">Contact</th>
                    <th className="py-2 pr-4">Terms</th>
                    <th className="py-2 pr-4 text-right">Lead</th>
                    <th className="py-2 pr-5 text-right">Min order</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const out = dropped.has(i);
                    const fate = plan.get(i);
                    return (
                      <tr key={i} className={`border-t border-hairline ${out ? 'opacity-45' : ''}`}
                          data-testid={`suppliers-import-row-${i}`}>
                        <td className="px-5 py-2">
                          <button type="button" onClick={() => toggle(i)}
                                  aria-pressed={!out}
                                  aria-label={out ? `Include ${row.name}` : `Leave out ${row.name}`}
                                  className={`grid h-5 w-5 place-items-center border-[1.5px] ${out ? 'border-hairline-strong bg-surface' : 'border-verlo bg-verlo text-white'}`}>
                            {out ? <X size={11} /> : <Check size={12} />}
                          </button>
                        </td>
                        <td className="py-2 pr-4">
                          <div className="font-medium text-ink">
                            {row.name || <span className="text-signal-stop">no name, so it will be skipped</span>}
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
                            {row.category && <span className="text-ink-soft">{row.category}</span>}
                            {/* Said per row, because "we already have this one" is the
                                difference between a correction and a duplicate. */}
                            {fate === 'update' && <span className="font-semibold text-ink-faint">already in your book, so it will be updated</span>}
                            {fate === 'duplicate' && <span className="font-semibold text-signal-warn">listed earlier in this file, so this row is skipped</span>}
                          </div>
                        </td>
                        <td className="py-2 pr-4 text-[12.5px] leading-snug">
                          {row.contact_name && <div className="text-ink">{row.contact_name}</div>}
                          {[row.email, row.phone].filter(Boolean).join(' · ')
                            || <span className="text-ink-faint">{absent(fate)}</span>}
                        </td>
                        <td className="py-2 pr-4 text-ink-soft">
                          {row.payment_terms
                            || <span className="text-ink-faint">{absent(fate, 'Net 30')}</span>}
                        </td>
                        <td className="py-2 pr-4 text-right font-mono tabular-nums text-ink-soft">
                          {numberIn(row.lead_time) ? `${numberIn(row.lead_time)}d` : <span className="text-ink-faint">{absent(fate)}</span>}
                        </td>
                        <td className="py-2 pr-5 text-right font-mono tabular-nums text-ink-soft">
                          {numberIn(row.min_order) ? gbp(numberIn(row.min_order)) : <span className="text-ink-faint">{absent(fate)}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-5 py-3">
              {done
                ? <Signal tone="ok" data-testid="suppliers-import-done">
                    {done.created} added{done.updated ? ` · ${done.updated} updated` : ''}
                    {done.skipped ? ` · ${done.skipped} skipped` : ''}
                  </Signal>
                : <span className="text-[12.5px] text-ink-faint">
                    Nothing is saved until you press the button. A supplier you already have
                    is updated rather than added again, and only the columns in this file
                    are touched.
                  </span>}
              <Button onClick={commit} disabled={busy || writing === 0 || Boolean(done)}
                      className="bg-verlo hover:bg-verlo-hover" data-testid="suppliers-import-commit">
                {busy ? 'Saving…' : `Save ${writing} ${writing === 1 ? 'supplier' : 'suppliers'}`}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SuppliersImportPage;
